/**
 * Operations (plan §18 "Operations"): release versions, readiness, migration
 * ledger, device sessions (online/ready distinct from paused/live/paper),
 * bindings + explicit live arming, backups, outbox and delivery health.
 */
import { useState } from "react";
import { Alert, Badge, Button, Group, Modal, Paper, SimpleGrid, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { upgradeApi } from "../../api/fleet";

function Stat({ label, value, ok }: { label: string; value: string | number; ok?: boolean }) {
  return (
    <Paper withBorder p="sm">
      <Text size="xs" c="dimmed">{label}</Text>
      <Group gap="xs"><Text fw={700}>{String(value)}</Text>{ok !== undefined && <Badge color={ok ? "green" : "red"} size="xs">{ok ? "ok" : "attention"}</Badge>}</Group>
    </Paper>
  );
}

export function OperationsPage() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ["ops", "status"], queryFn: () => upgradeApi.ops.status(), refetchInterval: 15_000 });
  const [arming, setArming] = useState<{ account_id: string; label: string } | null>(null);
  const [confirm, setConfirm] = useState("");
  const arm = useMutation({
    mutationFn: (p: { account_id: string; arm: boolean; confirm?: string }) => upgradeApi.ops.arm(p.account_id, { arm: p.arm, confirm: p.confirm }),
    onSuccess: (r) => { notifications.show({ color: r.armed ? "red" : "green", title: r.armed ? "Live execution ARMED for this binding" : "Binding disarmed", message: "" }); setArming(null); setConfirm(""); void qc.invalidateQueries({ queryKey: ["ops"] }); },
    onError: (e) => notifications.show({ color: "red", title: "Arming change refused", message: (e as Error).message }),
  });
  const s = status.data;
  if (!s) return <Text c="dimmed">Loading…</Text>;
  const count = (rows: Array<{ status: string; n: number }>, st: string) => rows.find((r) => r.status === st)?.n ?? 0;

  return (
    <>
      <Title order={2} mb="lg">Operations</Title>
      {!s.live_trading_armed_env && <Alert color="blue" mb="md" title="Installation is DISARMED">LIVE_TRADING_ARMED is not set on the engine; every live assignment executes as paper until the operator arms the installation and the specific binding.</Alert>}
      <SimpleGrid cols={{ base: 2, md: 4 }} mb="lg">
        <Stat label="Server release" value={s.release.server} />
        <Stat label="Shared package" value={String((s.release.shared as { version?: string }).version ?? "?")} />
        <Stat label="Migrations" value={`${s.migrations.applied} applied · ${s.migrations.pending.length} pending`} ok={s.migrations.pending.length === 0 && s.migrations.checksum_mismatches.length === 0} />
        <Stat label="Database size" value={s.database_size} />
        <Stat label="Signing key" value={s.readiness.signing_key_configured ? "configured" : "missing"} ok={s.readiness.signing_key_configured} />
        <Stat label="Engine token" value={s.readiness.engine_token_configured ? "configured" : "missing"} ok={s.readiness.engine_token_configured} />
        <Stat label="IFTTT key" value={s.readiness.ifttt_key_configured ? "configured" : "not set"} ok={s.readiness.ifttt_key_configured} />
        <Stat label="Outbox queued/sent" value={`${count(s.outbox, "queued")} / ${count(s.outbox, "sent")}`} />
        <Stat label="Deliveries exhausted" value={count(s.deliveries, "exhausted")} ok={count(s.deliveries, "exhausted") === 0} />
        <Stat label="Open critical incidents" value={s.open_incidents.find((i) => i.severity === "critical")?.n ?? 0} ok={!s.open_incidents.find((i) => i.severity === "critical")} />
      </SimpleGrid>

      <Title order={4} mb="xs">Devices & sessions</Title>
      <Paper withBorder p="xs" mb="lg">
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Bot</Table.Th><Table.Th>Release</Table.Th><Table.Th>Protocol</Table.Th><Table.Th>Connection</Table.Th><Table.Th>Session</Table.Th><Table.Th>Lease</Table.Th><Table.Th>Last seen</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {s.devices.map((d) => (
              <Table.Tr key={d.id}>
                <Table.Td>{d.display_name ?? d.legacy_agent_id}</Table.Td>
                <Table.Td>{d.release ?? "—"}</Table.Td>
                <Table.Td>{d.protocol_version ? `v${d.protocol_version}` : "v1 (legacy)"}</Table.Td>
                <Table.Td><Badge color={d.online ? "green" : "gray"}>{d.online ? "online" : "offline"}</Badge></Table.Td>
                <Table.Td>{d.session ? <Badge color={d.session.ready ? "green" : "yellow"}>{d.session.ready ? `ready (epoch ${d.session.epoch})` : `handshake (epoch ${d.session.epoch})`}</Badge> : <Badge color="gray">none</Badge>}</Table.Td>
                <Table.Td><Text size="xs">{d.lease_expires_at ? new Date(d.lease_expires_at).toLocaleTimeString() : "—"}</Text></Table.Td>
                <Table.Td><Text size="xs">{d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : "never"}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      <Title order={4} mb="xs">Account bindings (one active executor per account)</Title>
      <Paper withBorder p="xs" mb="lg">
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Account</Table.Th><Table.Th>Device</Table.Th><Table.Th>Binding epoch</Table.Th><Table.Th>Live arming</Table.Th><Table.Th /></Table.Tr></Table.Thead>
          <Table.Tbody>
            {s.bindings.map((b) => (
              <Table.Tr key={b.id}>
                <Table.Td>{b.label} <Text span size="xs" c="dimmed">{b.account_id}</Text></Table.Td>
                <Table.Td><Text size="xs">{b.device_id}</Text></Table.Td>
                <Table.Td>{b.epoch}</Table.Td>
                <Table.Td>{b.armed ? <Badge color="red">ARMED by {b.armed_by} · {b.armed_at ? new Date(b.armed_at).toLocaleString() : ""}</Badge> : <Badge color="gray">disarmed</Badge>}</Table.Td>
                <Table.Td>{b.armed
                  ? <Button size="compact-xs" variant="light" onClick={() => arm.mutate({ account_id: b.account_id, arm: false })}>Disarm</Button>
                  : <Button size="compact-xs" color="red" variant="light" onClick={() => setArming({ account_id: b.account_id, label: b.label })}>Arm live…</Button>}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      <Title order={4} mb="xs">Backups & restore rehearsals</Title>
      <Paper withBorder p="xs" mb="lg">
        {s.backups.length === 0 ? <Text c="dimmed" size="sm">No backup runs recorded yet (infra/scripts/backup.sh posts evidence here).</Text> : (
          <Table striped>
            <Table.Thead><Table.Tr><Table.Th>Kind</Table.Th><Table.Th>Status</Table.Th><Table.Th>Started</Table.Th><Table.Th>Location</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>{s.backups.map((b) => (
              <Table.Tr key={b.id}><Table.Td>{b.kind}</Table.Td><Table.Td><Badge color={b.status === "ok" ? "green" : "red"}>{b.status}</Badge></Table.Td><Table.Td>{new Date(b.started_at).toLocaleString()}</Table.Td><Table.Td><Text size="xs">{b.location ?? ""}</Text></Table.Td></Table.Tr>
            ))}</Table.Tbody>
          </Table>
        )}
      </Paper>

      <Modal opened={!!arming} onClose={() => setArming(null)} title={`Arm live execution — ${arming?.label ?? ""}`}>
        <Stack>
          <Alert color="red" title="Explicit approval required">A successful connection never authorizes live trading. Arming lets the bound device submit LIVE orders for this account once the engine installation is also armed and the assignment mode is live. Type <b>ARM LIVE TRADING</b> to confirm.</Alert>
          <TextInput value={confirm} onChange={(e) => setConfirm(e.currentTarget.value)} placeholder="ARM LIVE TRADING" />
          <Button color="red" disabled={confirm !== "ARM LIVE TRADING"} loading={arm.isPending} onClick={() => arming && arm.mutate({ account_id: arming.account_id, arm: true, confirm })}>Arm this binding</Button>
        </Stack>
      </Modal>
    </>
  );
}
